import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Card, CardContent } from '@/components/ui/card';
import { Calendar, Newspaper, Images, Contact, ArrowUpRight } from 'lucide-react';

const QuickLinks = () => {
  const links = [
    {
      title: 'Upcoming Events',
      description: 'Join our fellowship and diocesan programs.',
      icon: Calendar,
      path: '/events',
    },
    {
      title: 'Latest News',
      description: 'Announcements from the Bishop’s Court.',
      icon: Newspaper,
      path: '/news',
    },
    {
      title: 'Photo Gallery',
      description: 'Visual archives of our shared journey.',
      icon: Images,
      path: '/gallery',
    },
    {
      title: 'Contact Us',
      description: 'Reach out to the Diocesan Secretariat.',
      icon: Contact,
      path: '/contact',
    }
  ];

  return (
    <section className="py-24 bg-white relative overflow-hidden">
      {/* Decorative Gold Leaf Background Pattern - Adjusted for white bg */}
      <div className="absolute inset-0 opacity-[0.15] pointer-events-none">
        <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(#f59e0b_1.5px,transparent_1.5px)] [background-size:40px_40px]" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">

        {/* Header Section */}
        <div className="flex flex-col md:flex-row items-center justify-between mb-16 gap-8 text-center md:text-left">
          <div className="max-w-xl">
            <h2 className="text-sm font-bold uppercase tracking-[0.4em] text-amber-600 mb-4">
              Gateway
            </h2>
            <h2 className="text-4xl md:text-5xl font-serif font-bold text-slate-900">
              Diocesan Portal
            </h2>
          </div>
          <div className="h-px flex-grow bg-slate-200 mx-8 hidden md:block" />
          <p className="text-slate-500 font-serif italic text-lg max-w-xs">
            Quickly navigate to the most visited resources of our community.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {links.map((link, index) => {
            const IconComponent = link.icon;
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Link to={link.path} className="group block h-full">
                  <Card className="h-full bg-slate-50 border border-slate-100 rounded-[2rem] hover:bg-white hover:shadow-2xl hover:shadow-amber-500/10 hover:border-amber-500/50 transition-all duration-500 overflow-hidden relative">

                    {/* Index Number Decor - Adjusted for white bg */}
                    <span className="absolute top-6 right-8 text-5xl font-serif font-bold text-slate-200/50 group-hover:text-amber-500/10 transition-colors">
                      0{index + 1}
                    </span>

                    <CardContent className="p-10 flex flex-col h-full">
                      {/* Icon Box */}
                      <div className="w-14 h-14 bg-amber-500/10 rounded-2xl flex items-center justify-center mb-8 group-hover:bg-amber-500 transition-colors duration-500">
                        <IconComponent className="h-6 w-6 text-amber-600 group-hover:text-white transition-colors duration-500" />
                      </div>

                      <div className="space-y-3 mt-auto">
                        <h3 className="text-xl font-serif font-bold text-slate-900 group-hover:text-amber-600 transition-colors flex items-center gap-2">
                          {link.title}
                          <ArrowUpRight size={16} className="opacity-0 group-hover:opacity-100 transition-all -translate-y-1" />
                        </h3>
                        <p className="text-slate-500 text-sm leading-relaxed font-serif italic">
                          {link.description}
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default QuickLinks;